import { useContext, useEffect, useRef, useState } from 'react'
import { NavContext } from '../../../context/global_context'
import {
  createSearchParams,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import LinkWithStatus from '../../../components/common/LinkWithStatus'
import { deleteRequest, getRequest, postRequest } from '../../../services/base'
import Pagination from '../../../components/common/Pagination'
import NoRecordsFound from '../../../components/common/NoRecordsFound'
import UploadSvgImage from './UploadSvgImage'
import LoadingSpinnerOverlay from '../../../components/common/LoadingSpinnerOverlay'
import GenerateWiresButton from '../../../components/generate_wires_button'
import { getSessionStorageData } from '../../../utils/SessionStorage'

const PowerDistributionRow = ({
  id,
  name,
  wires_generated,
  svg,
  deletePowerDistribution,
  svg_image_filename,
  getPowerDistributions,
  onGenerateWiresClick,
}) => {
  return (
    <div className="flex flex-row items-center bg-white py-2 px-3 border-b border-tracer-dark-blue text-black">
      <div className="w-3/6 px-2 break-all">
        <div className="font-medium">{name}</div>
      </div>
      <UploadSvgImage
        id={id}
        svgImageFilename={svg}
        svgDisplayName={svg_image_filename}
        getPowerDistributions={() => getPowerDistributions()}
      />
      <div className="w-1/6 px-2 text-right">
        {wires_generated ? (
          <div className="font-medium">Wires generated</div>
        ) : (
          <a
            className="inline-block text-blue-500 underline"
            href="#"
            onClick={onGenerateWiresClick}
          >
            Generate Wires
          </a>
        )}
      </div>
      <div className="w-1/6 px-2 text-right">
        <a
          className="inline-block text-red-500 underline cursor-pointer"
          onClick={() => {
            deletePowerDistribution(id)
          }}
        >
          Delete
        </a>
      </div>
    </div>
  )
}

const PowerDistributions = (props) => {
  let { vehicle } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const [powerDistData, setPowerDistData] = useState([])
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    current_page: 1,
  })
  const [generateWireButtonStatus, setGenerateWireButtonStatus] =
    useState(false)

  const [searchQuery, setSearchQuery] = useState(
    searchParams.get('search') ? searchParams.get('search') : ''
  )

  const [value, dispatch] = useContext(NavContext)
  const vehicle_redirect_url = getSessionStorageData('vehicle')?.search
    ? `/vehicles/?${createSearchParams(getSessionStorageData('vehicle'))}`
    : `/vehicles`
  const currentUrl = useRef(
    `/api/power_distributions/?search=${searchQuery}&vehicle=${vehicle}`
  )

  useEffect(() => {
    dispatch({
      type: 'header',
      data: {
        header: 'Admin Tools',
        sub_header: `Vehicle List / ${vehicle} / Power Distributions`,
        id: '',
        show_back_btn: true,
        dtc_details: {},
        vehicle: '',
        back_btn_url: vehicle_redirect_url,
        full_width_container: false,
      },
    })
    getPowerDistributions(null)
    getGenerateWireButtonStatus()
  }, [])

  const getPowerDistributions = async (url = null) => {
    dispatch({
      type: 'loader',
      data: {
        caption: 'Loading Power distribution data',
        showLoader: true,
      },
      notification: {},
    })
    let response = await getRequest(currentUrl.current)
    setPowerDistData(response.results)
    setPagination(response.pagination)
    dispatch({
      type: 'loader',
      data: {},
      notification: {},
    })
  }

  const deletePowerDistribution = async (id) => {
    if (
      window.confirm(
        'Are you sure? You will delete selected Power Distribution'
      ) === true
    ) {
      dispatch({
        type: 'loader',
        data: {
          caption: 'Deleting Power distribution',
          showLoader: true,
        },
        notification: {},
      })
      let res = await deleteRequest(
        `/api/power_distributions/delete/${vehicle}/${id}/`
      )
      dispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: 'Power Distribution successfully deleted',
        },
      })
      getPowerDistributions()
    }
  }

  const onGenerateWiresClick = async (connectors = []) => {
    dispatch({
      type: 'loader',
      data: {
        caption: 'Processing wire generation',
        showLoader: true,
      },
      notification: {},
    })

    let res = await postRequest(`/api/wire/generate_wires/`, {
      vehicle: vehicle,
      connectors: connectors,
      clean_wires: false,
      is_inspection_points_wire: false,
      is_power_distribution_wire: true,
    })
    dispatch({
      type: 'notification',
      data: {
        notification_type: 'success',
        msg: 'Wire Generation successfully started',
      },
    })
    getGenerateWireButtonStatus()
  }

  const getGenerateWireButtonStatus = async () => {
    let response = await getRequest(
      `/api/dtcs/generate_wire_button_status/?vehicle=${vehicle}`
    )
    setGenerateWireButtonStatus(response.show_loader)
    if (response.show_loader) {
      setTimeout(() => {
        getGenerateWireButtonStatus()
      }, 7000)
    }
  }

  const downloadPackage = async () => {
    dispatch({
      type: 'loader',
      data: {
        caption: 'Downloading Power Distribution Package',
        showLoader: true,
      },
      notification: {},
    })

    let formData = new FormData()
    let res = await postRequest(
      `/api/power_distributions/download_package/${vehicle}/`,
      formData,
      true
    )
    if (res?.msg) {
      dispatch({
        type: 'notification',
        data: { notification_type: 'error', msg: res?.msg },
      })
    } else {
      const url = window.URL.createObjectURL(new Blob([res]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `power_distribution.zip`)
      document.body.appendChild(link)
      link.click()
      link.remove()

      dispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: 'File downloaded successfully',
          loader: true,
        },
      })
    }
  }

  return (
    <>
      {generateWireButtonStatus && (
        <div>
          <LoadingSpinnerOverlay
            active={generateWireButtonStatus}
            text={'Wire Generation is in Progress'}
          />
        </div>
      )}
      <div className="flex flex-col pt-3 flex-grow">
        <form
          className="flex flex-col w-full space-y-2 pb-5"
          action={`/vehicles/${vehicle}/power_distributions`}
          acceptCharset="UTF-8"
          method="get"
        >
          <div className="flex space-x-5">
            <div className="w-2/3">
              <input
                autoComplete="off"
                className="w-full bg-white h-10 px-5 pr-5 rounded focus:outline-none text-black"
                placeholder="Enter Connector's Name"
                type="search"
                name="search"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                }}
              />
            </div>
            <div className="w-2/3">
              <button className="bg-tracer-lt-blue font-light hover:bg-blue-700 text-white py-2 px-3 rounded">
                Search
              </button>
            </div>
            <div className="w-2/3 flex justify-end font-light space-x-5">
              <LinkWithStatus
                href={`/vehicles/new`}
                className={
                  'cursor-pointer inline-block bg-tracer-lt-blue font-light hover:bg-blue-700 text-white py-2 px-3 rounded'
                }
                title="Download Package"
                onClickFunc={() => {
                  downloadPackage()
                }}
              />
              <GenerateWiresButton
                is_power_distribution_wire={true}
                is_inspection_points_wire={false}
              />
            </div>
          </div>
        </form>
        <div className="flex flex-row bg-tracer-lt-blue rounded-t text-white px-3 text-sm font-light">
          <div className="w-full px-2">Power Distribution</div>
        </div>

        {powerDistData.map((power_distribution, index) => (
          <PowerDistributionRow
            {...power_distribution}
            key={index}
            deletePowerDistribution={(id) => deletePowerDistribution(id)}
            getPowerDistributions={() => getPowerDistributions(null)}
            onGenerateWiresClick={() =>
              onGenerateWiresClick([power_distribution.connector])
            }
          />
        ))}
        {powerDistData?.length < 1 && <NoRecordsFound loading={false} />}
        <div className="py-6">
          <Pagination
            context={pagination}
            onClickNext={(e) => {
              currentUrl.current = e
              getPowerDistributions()
            }}
            onClickPrev={(e) => {
              currentUrl.current = e
              getPowerDistributions(e)
            }}
          />
        </div>
      </div>
    </>
  )
}

export default PowerDistributions
